
import { useState } from "react";
import { toast } from "sonner";
import { ExportFormat } from "@/services/export/types";
import { exportData } from "@/services/export/exportService";
import { exportToAvemFormat } from "@/services/export/avemExport";
import { supabase } from "@/integrations/supabase/client";

export const useExports = () => {
  const [exportFormat, setExportFormat] = useState<ExportFormat>("excel");
  const [exportInProgress, setExportInProgress] = useState<boolean>(false);

  // Récupération des données à exporter avec gestion d'erreur améliorée
  const fetchExportData = async (dateRange: { from: Date; to: Date }, 
                                selectedChantiers: string[], 
                                selectedEmployes: string[]) => {
    try {
      console.log("🔍 Récupération des données d'export...");
      
      // Vérification préliminaire de la connexion Supabase
      if (!supabase) {
        console.error("Client Supabase non initialisé");
        throw new Error("Erreur de configuration Supabase");
      }

      // Appel à la fonction Edge
      const { data, error } = await supabase.functions.invoke('get-export-data', {
        body: {
          filters: {
            dateRange,
            selectedChantiers,
            selectedEmployes
          }
        }
      });

      if (error) {
        console.error("Erreur lors de la récupération des données:", error);
        throw new Error("Erreur lors de la récupération des données: " + error.message);
      }

      console.log("✅ Données récupérées:", data?.data?.length || 0, "entrées");
      return data.data || [];
    } catch (error) {
      console.error("Erreur lors de l'appel à l'edge function:", error);
      throw error;
    }
  };

  // Fonction pour exporter les données
  const handleExport = async (dateRange: { from: Date; to: Date }, 
                             selectedChantiers: string[], 
                             selectedEmployes: string[]): Promise<void> => {
    try {
      setExportInProgress(true);
      toast.info(`Préparation de l'export en ${exportFormat.toUpperCase()}...`);
      
      // Récupérer les données à exporter
      const dataToExport = await fetchExportData(dateRange, selectedChantiers, selectedEmployes);
      
      if (!dataToExport || dataToExport.length === 0) {
        toast.warning("Aucune donnée à exporter pour la période sélectionnée");
        return;
      }
      
      let success = false;
      
      // Export au format AVEM pour Excel
      if (exportFormat === "excel") {
        const fileName = `feuille-pointage-avem-${dateRange.from.toISOString().split('T')[0]}-au-${dateRange.to.toISOString().split('T')[0]}`;
        success = await exportToAvemFormat(dataToExport, fileName);
      } else {
        // Autres formats (CSV, PDF) - utiliser l'ancien système
        const exportOptions = {
          fileName: `rapport-${dateRange.from.toISOString().split('T')[0]}-au-${dateRange.to.toISOString().split('T')[0]}`,
          includeHeaders: true
        };
        
        success = await exportData(dataToExport, exportFormat, exportOptions);
      }
      
      if (success) {
        toast.success(`Exportation en ${exportFormat.toUpperCase()} réussie`);
      } else {
        toast.error(`Erreur lors de l'export en ${exportFormat.toUpperCase()}`);
      }
      
      setExportInProgress(false);
      return Promise.resolve();
    } catch (error) {
      console.error("Erreur d'export:", error);
      toast.error("Une erreur est survenue lors de l'export: " + (error as Error).message);
      setExportInProgress(false);
      throw error;
    }
  };

  // Fonction pour imprimer le rapport
  const handlePrintReport = async (dateRange: { from: Date; to: Date }, 
                                  selectedChantiers: string[], 
                                  selectedEmployes: string[]) => {
    try {
      // Récupérer les données pour impression
      const data = await fetchExportData(dateRange, selectedChantiers, selectedEmployes);
      
      if (!data || data.length === 0) {
        toast.warning("Aucune donnée à imprimer pour la période sélectionnée");
        return;
      }
      
      // Préparer l'impression
      toast.success("Préparation de l'impression...");
      
      // Ouvrir une nouvelle fenêtre pour l'impression
      const printWindow = window.open('', '_blank');
      
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Rapport d'activité</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; }
                h1 { color: #333; }
                .header { margin-bottom: 20px; }
                .footer { margin-top: 30px; font-size: 12px; color: #666; }
                @media print {
                  .no-print { display: none; }
                }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>Rapport d'activité</h1>
                <p>Période: ${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}</p>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Employé</th>
                    <th>Chantier</th>
                    <th>Date</th>
                    <th>Entrée</th>
                    <th>Sortie</th>
                    <th>Total</th>
                    <th>Commentaire</th>
                  </tr>
                </thead>
                <tbody>
                  ${data.map(row => `
                    <tr>
                      <td>${row.employé || ''}</td>
                      <td>${row.chantier || ''}</td>
                      <td>${row.date || ''}</td>
                      <td>${row.entrée || ''}</td>
                      <td>${row.sortie || ''}</td>
                      <td>${row.total || ''}</td>
                      <td>${row.commentaire || ''}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
              <div class="footer">
                <p>Rapport généré le ${new Date().toLocaleDateString()} à ${new Date().toLocaleTimeString()}</p>
              </div>
              <div class="no-print">
                <button onclick="window.print()">Imprimer</button>
              </div>
              <script>
                setTimeout(() => {
                  window.print();
                }, 1000);
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
      } else {
        toast.error("Impossible d'ouvrir la fenêtre d'impression");
      }
      
      toast.success("Document prêt à imprimer!");
    } catch (error) {
      console.error("Erreur lors de la préparation de l'impression:", error);
      toast.error("Une erreur est survenue lors de la préparation de l'impression");
    }
  };

  return {
    exportFormat,
    setExportFormat,
    exportInProgress,
    handleExport,
    handlePrintReport
  };
};
